import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton // Add IconButton import
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close'; // Add CloseIcon import

/**
 * OnboardingTour component that guides new users through the main features
 * of the application after they've registered and subscribed
 */
const OnboardingTour = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = () => {
    // Navigate to dashboard when onboarding is complete
    navigate('/dashboard');
  };
  
  // New function to handle skipping the onboarding
  const handleSkip = () => {
    navigate('/dashboard');
  };

  const steps = [
    {
      label: 'Welcome to Our Platform',
      description: "Thank you for subscribing! Let's get you started",
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Card sx={{ mb: 3 }}>
            <CardMedia
              component="img"
              height="240"
              image="/welcome-banner.jpg"
              alt="Welcome banner"
              sx={{ bgcolor: '#e3f2fd' }}
            />
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                Your journey starts here
              </Typography>
              <Typography variant="body1">
                Our platform uses cutting-edge computer vision technology to analyze technical drawings and schematics. This short tour will guide you through the main features and how to get the most out of our service.
              </Typography>
            </CardContent>
          </Card>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              This tour will take about 2 minutes to complete
            </Typography>
            <Button 
              variant="contained" 
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
            >
              Start Tour
            </Button>
          </Box>
        </Box>
      )
    },
    {
      label: 'Upload Your Drawings',
      description: 'Learn how to upload and manage your technical drawings.',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <CloudUploadIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Step 1:</strong> Navigate to the Upload page from your dashboard.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <CloudUploadIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Step 2:</strong> Drag & drop your PDF or image files into the upload area.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <CloudUploadIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Step 3:</strong> Wait for the analysis to complete.
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Supported formats: PDF, PNG, JPG, TIFF
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f9f9f9',
                  p: 2
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography>Upload Interface Preview</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack}>Back</Button>
            <Button variant="contained" onClick={handleNext}>Next</Button>
          </Box>
        </Box>
      )
    },
    {
      label: 'View Analysis Results',
      description: 'Understand how to interpret the analysis results.',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f9f9f9',
                  p: 2
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <AnalyticsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography>Results Interface Preview</Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <AnalyticsIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Element Detection:</strong> The system identifies all components in your drawing.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <AnalyticsIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Connection Analysis:</strong> Shows how components are connected.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <AnalyticsIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Interactive Visualization:</strong> Click on elements to see details.
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack}>Back</Button>
            <Button variant="contained" onClick={handleNext}>Next</Button>
          </Box>
        </Box>
      )
    },
    {
      label: 'Edit and Customize',
      description: 'Learn how to edit and refine the analysis results.',
      content: (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <EditIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Edit Elements:</strong> Correct any misidentified components.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <EditIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Modify Connections:</strong> Add or remove connections between elements.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, mb: 2, borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <EditIcon color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  <strong>Save Changes:</strong> All edits are automatically saved to your account.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f9f9f9',
                  p: 2
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <EditIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography>Edit Interface Preview</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleBack}>Back</Button>
            <Button variant="contained" onClick={handleNext}>Next</Button>
          </Box>
        </Box>
      )
    },
    {
      label: 'You\'re All Set!',
      description: 'You\'re ready to start using our platform.',
      content: (
        <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Congratulations!
          </Typography>
          <Typography variant="body1" paragraph>
            You've completed the onboarding tour and are ready to start analyzing your technical drawings.
          </Typography>
          <Typography variant="body1" paragraph>
            Remember, each document analysis costs $10, which will be charged to your payment method on file.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={handleComplete}
            sx={{ mt: 3 }}
          >
            Go to Dashboard
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 8, textAlign: 'center', position: 'relative' }}>
        {/* Add close button in the top right corner */}
        <IconButton
          aria-label="skip onboarding"
          onClick={handleSkip}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            color: 'grey.600',
            '&:hover': {
              color: 'grey.900',
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
        
        <Typography variant="h3" component="h1" gutterBottom>
          Welcome Onboard!
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Let's get you started with our platform
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 4 }, mb: 8 }}>
        {/* Add a small skip notice at the top of the Paper */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="text" 
            color="primary" 
            size="small" 
            onClick={handleSkip}
            startIcon={<CloseIcon />}
          >
            Skip tour
          </Button>
        </Box>
        
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="h6">{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {step.description}
                </Typography>
                {step.content}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </Container>
  );
};

export default OnboardingTour;